// Theme-driven static scene art. Painted NATIVELY at the lo-res 320x224 grid
// (the brief's original Sega art resolution) then upscaled x2 nearest-neighbor
// into the 640x448 sprite, so every art pixel is a chunky block. A grain pass
// dirties up the flat fills. Text is drawn separately at full 640 res so it
// stays sharp. A future "level" just supplies a different Theme.
//
// Slot rects are stored in 640x448 BUFFER space (gameplay/hit-test space) and
// divided down when painting. Windows are intentionally small, varied in size,
// off-grid, and slightly rotated — crooked and hard to aim at.

import type { ExteriorArt } from "./houseArt";

export interface SlotRect {
  x: number;
  y: number;
  w: number;
  h: number;
  kind: "window" | "door";
  rot?: number; // degrees of tilt for a distorted, crooked look
  emergeOffset?: number; // px the emerging zombie sits above the slot bottom
}

// Optional cutouts from house.png used by the exterior; set by main.ts before
// baking. Null -> the exterior falls back to the procedural house/moon.
let exteriorArt: ExteriorArt | null = null;
export function setExteriorArt(art: ExteriorArt | null) {
  exteriorArt = art;
}

export interface Theme {
  name: string;
  sky: [string, string]; // top -> bottom gradient (or wall, for interiors)
  moon: string;
  wood: { dark: string; mid: string; light: string };
  windowDark: string;
  windowGlow: string; // slot interior glow (amber outside, blood-red in the hall)
  ground: [string, string]; // top -> bottom gradient (yard, or floor for interiors)
  groundY: number; // buffer-space y where the wall ends and the floor/yard begins
  slots: SlotRect[];
  // Bakes this theme's full lo-res scene (320x224) into ctx. A new level just
  // supplies a different paint routine; gameplay only reads groundY + slots.
  paint: (ctx: CanvasRenderingContext2D, theme: Theme) => void;
  // Optional full-res (640x448) overlay drawn after the lo-res upscale — used so
  // the detailed house.png art keeps its resolution instead of being halved.
  paintHi?: (ctx: CanvasRenderingContext2D, theme: Theme) => void;
  // Optional flickering wall candles (buffer coords), drawn live by the scene.
  candles?: { x: number; y: number }[];
}

// Composited hall backdrop (2ndLEvel.png + floor), set by main.ts before baking.
let hallArt: HTMLCanvasElement | null = null;
export function setHallArt(c: HTMLCanvasElement | null) {
  hallArt = c;
}
function paintHallHi(ctx: CanvasRenderingContext2D) {
  if (hallArt) ctx.drawImage(hallArt, 0, 0);
}

// Default night-time theme — the brief's palette. Slots are small, mismatched,
// off-axis, and crooked (640-space coords; rot in degrees).
export const NIGHT_THEME: Theme = {
  name: "night",
  sky: ["#160a2e", "#3a1d5c"],
  moon: "#f4e8c1",
  wood: { dark: "#241a12", mid: "#3d2c1c", light: "#5a432a" },
  windowDark: "#07060f",
  windowGlow: "#e0a52a",
  ground: ["#1a2410", "#0e1608"],
  groundY: 340,
  slots: [
    { x: 74, y: 78, w: 54, h: 62, kind: "window", rot: -7 }, // upper-left, tilted
    { x: 286, y: 68, w: 46, h: 56, kind: "window", rot: 5 }, // upper-mid, small
    { x: 498, y: 94, w: 60, h: 50, kind: "window", rot: -3 }, // upper-right, squat
    { x: 104, y: 216, w: 48, h: 60, kind: "window", rot: 6 }, // lower-left
    { x: 292, y: 204, w: 54, h: 108, kind: "door", rot: -2 }, // center door, narrow
    { x: 470, y: 224, w: 44, h: 66, kind: "window", rot: 8 }, // lower-right, tall
  ],
  paint: paintNightScene,
  paintHi: paintNightHi,
};

// Level 2 — gothic blood-red interior hall. Same groundY (floor line) so all
// walker/boss geometry is unchanged; only the art + slot layout differ. Balcony
// slots sit high so those zombies fall over the railing down to the floor.
export const HALL_THEME: Theme = {
  name: "hall",
  sky: ["#1a0608", "#380a10"], // back wall: near-black -> dark crimson
  moon: "#7a1418", // (unused indoors; kept for type) dim red
  wood: { dark: "#160a0c", mid: "#2c1416", light: "#5a2a24" }, // black timber / crimson highlight
  windowDark: "#0a0406",
  windowGlow: "#b8281f", // doorways glow blood-red instead of amber (procedural fallback)
  ground: ["#1c1012", "#0c0708"], // dark board floor
  groundY: 337, // where the wood floor meets the back wall (from the art)
  // Slots aligned to the teal openings in 2ndLEvel.png: the wide central upper
  // opening split into 3 doors, the 2 balcony side windows, 2 lower windows.
  slots: [
    { x: 256, y: 33, w: 42, h: 92, kind: "door", emergeOffset: -3 }, // upper centre — left door (feet 3px below bottom)
    { x: 300, y: 33, w: 42, h: 92, kind: "door", emergeOffset: -3 }, // upper centre — middle door
    { x: 344, y: 33, w: 42, h: 92, kind: "door", emergeOffset: -3 }, // upper centre — right door
    { x: 158, y: 34, w: 40, h: 80, kind: "window", emergeOffset: 57 }, // upper-left balcony window
    { x: 446, y: 34, w: 40, h: 80, kind: "window", emergeOffset: 57 }, // upper-right balcony window
    { x: 147, y: 188, w: 50, h: 99, kind: "window", emergeOffset: 22 }, // lower-left window (3px left)
    { x: 445, y: 188, w: 50, h: 99, kind: "window", emergeOffset: 22 }, // lower-right window (3px right)
  ],
  candles: [
    { x: 52, y: 78 },
    { x: 588, y: 78 },
    { x: 52, y: 182 },
    { x: 588, y: 182 },
  ],
  paint: paintHallScene, // procedural fallback if the art fails to load
  paintHi: paintHallHi,
};

// All levels in order (index = level). Graveyard (level 3) appended later.
export const THEMES: Theme[] = [NIGHT_THEME, HALL_THEME];

export const BG_WIDTH = 640;
export const BG_HEIGHT = 448;
const PIXEL = 2; // art is painted at BG/PIXEL then upscaled nearest x PIXEL
const LW = BG_WIDTH / PIXEL; // 320
const LH = BG_HEIGHT / PIXEL; // 224

// --- small helpers -------------------------------------------------------

function hexToRgb(hex: string): [number, number, number] {
  const h = hex.replace("#", "");
  return [
    parseInt(h.slice(0, 2), 16),
    parseInt(h.slice(2, 4), 16),
    parseInt(h.slice(4, 6), 16),
  ];
}

function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

function clamp8(v: number): number {
  return v < 0 ? 0 : v > 255 ? 255 : v;
}

// Deterministic per-pixel hash in [0,1) for the grain pass.
function hash2(x: number, y: number): number {
  let n = (x * 374761393 + y * 668265263) | 0;
  n = (n ^ (n >> 13)) * 1274126177;
  return ((n ^ (n >> 16)) >>> 0) / 4294967295;
}

// 4x4 ordered (Bayer) dither thresholds, normalized to (0,1).
const BAYER4 = [
  [0, 8, 2, 10],
  [12, 4, 14, 6],
  [3, 11, 1, 9],
  [15, 7, 13, 5],
].map((row) => row.map((v) => (v + 0.5) / 16));

// --- painters (all in LO-RES 320x224 space) ------------------------------

// Per-pixel dithered vertical gradients for the sky (above gy) and yard.
function paintSkyGround(ctx: CanvasRenderingContext2D, theme: Theme) {
  const img = ctx.createImageData(LW, LH);
  const d = img.data;
  const [str, stg, stb] = hexToRgb(theme.sky[0]);
  const [sbr, sbg, sbb] = hexToRgb(theme.sky[1]);
  const [gar, gag, gab] = hexToRgb(theme.ground[0]);
  const [gbr, gbg, gbb] = hexToRgb(theme.ground[1]);
  const skySteps = 10;
  const groundSteps = 5;
  const gy = Math.floor(theme.groundY / PIXEL);

  for (let y = 0; y < LH; y++) {
    for (let x = 0; x < LW; x++) {
      const m = BAYER4[y & 3][x & 3];
      let r: number;
      let g: number;
      let b: number;
      if (y < gy) {
        const t = y / (gy - 1);
        const sc = t * (skySteps - 1);
        const lo = Math.floor(sc);
        const idx = (sc - lo > m ? lo + 1 : lo) / (skySteps - 1);
        r = lerp(str, sbr, idx);
        g = lerp(stg, sbg, idx);
        b = lerp(stb, sbb, idx);
      } else {
        const t = (y - gy) / Math.max(1, LH - gy - 1);
        const sc = t * (groundSteps - 1);
        const lo = Math.floor(sc);
        const idx = (sc - lo > m ? lo + 1 : lo) / (groundSteps - 1);
        r = lerp(gar, gbr, idx);
        g = lerp(gag, gbg, idx);
        b = lerp(gab, gbb, idx);
      }
      const i = (y * LW + x) * 4;
      d[i] = r;
      d[i + 1] = g;
      d[i + 2] = b;
      d[i + 3] = 255;
    }
  }
  ctx.putImageData(img, 0, 0);
}

// A handful of fixed stars in the upper sky band (lo-res coords).
function paintStars(ctx: CanvasRenderingContext2D) {
  const stars: Array<[number, number]> = [
    [30, 9], [62, 15], [103, 7], [168, 5], [207, 11],
    [235, 20], [278, 8], [301, 17], [12, 21], [146, 13],
  ];
  ctx.fillStyle = "#cdbff0";
  for (const [sx, sy] of stars) ctx.fillRect(sx, sy, 1, 1);
}

function paintMoon(ctx: CanvasRenderingContext2D, theme: Theme) {
  const mx = 256;
  const my = 23;
  const mr = 12;
  const glow = ctx.createRadialGradient(mx, my, 1, mx, my, mr * 2.6);
  glow.addColorStop(0, "rgba(244,232,193,0.5)");
  glow.addColorStop(1, "rgba(244,232,193,0)");
  ctx.fillStyle = glow;
  ctx.beginPath();
  ctx.arc(mx, my, mr * 2.6, 0, Math.PI * 2);
  ctx.fill();
  ctx.fillStyle = theme.moon;
  ctx.beginPath();
  ctx.arc(mx, my, mr, 0, Math.PI * 2);
  ctx.fill();
  ctx.fillStyle = "rgba(0,0,0,0.08)";
  ctx.beginPath();
  ctx.arc(mx - 4, my - 3, 3, 0, Math.PI * 2);
  ctx.fill();
  ctx.beginPath();
  ctx.arc(mx + 4, my + 3, 4, 0, Math.PI * 2);
  ctx.fill();
}

// The house: gable roof + planked wall with light/shadow edges (lo-res).
function paintHouse(ctx: CanvasRenderingContext2D, theme: Theme) {
  const wallX = 24;
  const wallR = 296;
  const wallY = 29;
  const wallB = Math.floor(theme.groundY / PIXEL);
  const wallW = wallR - wallX;

  ctx.fillStyle = theme.wood.mid;
  ctx.fillRect(wallX, wallY, wallW, wallB - wallY);

  ctx.fillStyle = theme.wood.dark;
  for (let y = wallY + 13; y < wallB; y += 14) ctx.fillRect(wallX, y, wallW, 1);

  ctx.fillStyle = "rgba(0,0,0,0.18)";
  for (let x = wallX + 32; x < wallR; x += 32) ctx.fillRect(x, wallY, 1, wallB - wallY);

  ctx.fillStyle = theme.wood.light;
  ctx.fillRect(wallX, wallY, wallW, 2);
  ctx.fillRect(wallX, wallY, 2, wallB - wallY);
  ctx.fillStyle = theme.wood.dark;
  ctx.fillRect(wallR - 2, wallY, 2, wallB - wallY);
  ctx.fillRect(wallX, wallB - 2, wallW, 2);

  ctx.fillStyle = theme.wood.dark;
  ctx.beginPath();
  ctx.moveTo(wallX - 7, wallY + 2);
  ctx.lineTo(160, 10);
  ctx.lineTo(wallR + 7, wallY + 2);
  ctx.closePath();
  ctx.fill();
  ctx.strokeStyle = theme.wood.light;
  ctx.lineWidth = 1;
  ctx.stroke();
}

// One window/door, drawn rotated about its center for a crooked look (lo-res).
function drawSlot(ctx: CanvasRenderingContext2D, slot: SlotRect, theme: Theme) {
  const cx = (slot.x + slot.w / 2) / PIXEL;
  const cy = (slot.y + slot.h / 2) / PIXEL;
  const w = slot.w / PIXEL;
  const h = slot.h / PIXEL;
  const fb = 3;
  const x = -w / 2;
  const y = -h / 2;

  ctx.save();
  ctx.translate(cx, cy);
  ctx.rotate(((slot.rot ?? 0) * Math.PI) / 180);

  // beveled wood frame
  ctx.fillStyle = theme.wood.light;
  ctx.fillRect(x - fb, y - fb, w + fb * 2, h + fb * 2);
  ctx.fillStyle = theme.wood.dark;
  ctx.fillRect(x - fb + 1, y - fb + 1, w + fb * 2 - 2, h + fb * 2 - 2);

  // dark interior
  ctx.fillStyle = theme.windowDark;
  ctx.fillRect(x, y, w, h);

  // amber glow from within (brighter at top, in the rotated frame)
  const glow = ctx.createLinearGradient(0, y, 0, y + h);
  glow.addColorStop(0, theme.windowGlow);
  glow.addColorStop(0.55, "rgba(224,165,42,0.5)");
  glow.addColorStop(1, "rgba(120,80,10,0.12)");
  ctx.fillStyle = glow;
  ctx.fillRect(x, y, w, h);

  // thin mullions
  ctx.fillStyle = theme.wood.dark;
  if (slot.kind === "window") {
    ctx.fillRect(x, -1, w, 1);
    ctx.fillRect(-1, y, 1, h);
  } else {
    ctx.fillRect(-1, y, 1, h);
    ctx.fillRect(x + 3, y + 6, w - 6, 1);
    ctx.fillRect(x + 3, y + h - 12, w - 6, 1);
  }

  ctx.restore();
}

// A few grass blades along the yard edge (lo-res).
function paintGroundDetail(ctx: CanvasRenderingContext2D, theme: Theme) {
  const gy = Math.floor(theme.groundY / PIXEL);
  ctx.fillStyle = theme.wood.dark;
  ctx.fillRect(0, gy - 1, LW, 1);

  ctx.fillStyle = "#243314";
  for (let x = 4; x < LW; x += 13) {
    const hgt = 2 + ((x * 7) % 3);
    ctx.fillRect(x, gy + 1, 1, hgt);
    ctx.fillRect(x + 3, gy + 2, 1, hgt - 1);
  }
}

// Subtle per-pixel grain to break up the flat fills.
function applyGrain(ctx: CanvasRenderingContext2D) {
  const img = ctx.getImageData(0, 0, LW, LH);
  const d = img.data;
  for (let y = 0; y < LH; y++) {
    for (let x = 0; x < LW; x++) {
      const delta = (hash2(x, y) - 0.5) * 18;
      const i = (y * LW + x) * 4;
      d[i] = clamp8(d[i] + delta);
      d[i + 1] = clamp8(d[i + 1] + delta);
      d[i + 2] = clamp8(d[i + 2] + delta);
    }
  }
  ctx.putImageData(img, 0, 0);
}

// Full-res (640x448) placement of the house.png cutout: centred, base on the
// ground line, enlarged so the roof is clipped a little by the top edge.
const HOUSE_W = 500; // tuned with the human against a rendered preview
const MOON_W = 104;
const TREE_W = 178;
const TREE_CUT = 58; // px each tree runs off its screen side

function houseRectBuf(art: ExteriorArt) {
  const w = HOUSE_W;
  const h = w * (art.house.height / art.house.width);
  return { x: (BG_WIDTH - w) / 2, y: NIGHT_THEME.groundY - h, w, h };
}

// Composite the house.png cutouts at full resolution: moon top-right (soft glow,
// the house may overlap it), the enlarged house centred with its base on the
// ground line, and the bare tree on each side (mirrored), cut by the edges.
function paintNightHi(ctx: CanvasRenderingContext2D) {
  const art = exteriorArt;
  if (!art) return;
  ctx.imageSmoothingEnabled = true;
  const BW = BG_WIDTH;
  const gy = NIGHT_THEME.groundY;

  // moon, top-right, with a soft glow
  const mw = MOON_W;
  const mh = mw * (art.moon.height / art.moon.width);
  const mx = BW - mw - 14;
  const cxp = mx + mw / 2;
  const cyp = 10 + mh / 2;
  const glow = ctx.createRadialGradient(cxp, cyp, 3, cxp, cyp, mw);
  glow.addColorStop(0, "rgba(244,232,193,0.30)");
  glow.addColorStop(1, "rgba(244,232,193,0)");
  ctx.fillStyle = glow;
  ctx.fillRect(cxp - mw, cyp - mw, mw * 2, mw * 2);
  ctx.drawImage(art.moon, mx, 10, mw, mh);

  // house (drawn over the moon)
  const r = houseRectBuf(art);
  ctx.drawImage(art.house, r.x, r.y, r.w, r.h);

  // trees: base on the ground line, cut by the screen sides; right is mirrored
  const tw = TREE_W;
  const th = tw * (art.tree.height / art.tree.width);
  const ty = gy - th;
  ctx.drawImage(art.tree, -TREE_CUT, ty, tw, th);
  ctx.save();
  ctx.translate(BW + TREE_CUT, ty);
  ctx.scale(-1, 1);
  ctx.drawImage(art.tree, 0, 0, tw, th);
  ctx.restore();
}

// Map the art's auto-detected window/door rects (house-local px) to buffer-space
// slot rects, using the same placement as paintNightHi (so slots line up).
export function deriveExteriorSlots(art: ExteriorArt): SlotRect[] {
  const r = houseRectBuf(art);
  const sx = r.w / art.house.width;
  const sy = r.h / art.house.height;
  const slots: SlotRect[] = art.windows.map((win) => ({
    x: Math.round(r.x + win.x * sx),
    y: Math.round(r.y + win.y * sy),
    w: Math.round(win.w * sx),
    h: Math.round(win.h * sy),
    kind: win.kind,
  }));

  // Hand-tuned per-window vertical placement so each zombie sits in its glass.
  // Emerging zombies draw at the slot CENTRE, so "same level" aligns centres.
  const cen = (s: SlotRect) => s.y + s.h / 2;
  const setCen = (s: SlotRect, cy: number) => (s.y = Math.round(cy - s.h / 2));
  const midY = r.y + r.h * 0.5;
  const upper = slots.filter((s) => cen(s) < midY).sort((a, b) => a.x - b.x);
  const lower = slots.filter((s) => cen(s) >= midY).sort((a, b) => a.x - b.x);

  // Upper floor (left→right): 1st as-is; 2nd 4px higher; 3rd & 4th aligned to the 2nd.
  if (upper.length >= 4) {
    upper[0].y += 7;
    upper[1].y += 6;
    setCen(upper[2], cen(upper[1]));
    setCen(upper[3], cen(upper[1]));
  }
  // Lower floor (left→right: window, window, door, window): 1st +22 & tilted 4°
  // right; 2nd +6 (3px up); door +9; 4th (last) aligned to the 2nd (so also up).
  if (lower.length >= 4) {
    lower[0].y += 22;
    lower[0].rot = 4;
    lower[1].y += 6;
    lower[2].y += 9;
    setCen(lower[3], cen(lower[1]));
  }

  return slots;
}

// Exterior night composition (lo-res). With the house.png art the house/moon/
// trees are drawn full-res in paintNightHi instead; here we only do sky, stars,
// ground and grain. Without the art we fall back to the procedural house.
function paintNightScene(ctx: CanvasRenderingContext2D, theme: Theme) {
  paintSkyGround(ctx, theme);
  paintStars(ctx);
  if (!exteriorArt) {
    paintMoon(ctx, theme);
    paintHouse(ctx, theme);
    for (const slot of theme.slots) drawSlot(ctx, slot, theme);
  }
  paintGroundDetail(ctx, theme);
  applyGrain(ctx);
}

// --- Hall (level 2) painters, lo-res 320x224 ----------------------------

// Interior wall (above gy) + board floor (below gy), dithered like the exterior.
function paintHallWallFloor(ctx: CanvasRenderingContext2D, theme: Theme) {
  const img = ctx.createImageData(LW, LH);
  const d = img.data;
  const [wtr, wtg, wtb] = hexToRgb(theme.sky[0]);
  const [wbr, wbg, wbb] = hexToRgb(theme.sky[1]);
  const [ftr, ftg, ftb] = hexToRgb(theme.ground[0]);
  const [fbr, fbg, fbb] = hexToRgb(theme.ground[1]);
  const gy = Math.floor(theme.groundY / PIXEL);
  const wallSteps = 8;
  const floorSteps = 5;
  for (let y = 0; y < LH; y++) {
    for (let x = 0; x < LW; x++) {
      const m = BAYER4[y & 3][x & 3];
      let r: number;
      let g: number;
      let b: number;
      if (y < gy) {
        const t = y / (gy - 1);
        const sc = t * (wallSteps - 1);
        const lo = Math.floor(sc);
        const idx = (sc - lo > m ? lo + 1 : lo) / (wallSteps - 1);
        r = lerp(wtr, wbr, idx);
        g = lerp(wtg, wbg, idx);
        b = lerp(wtb, wbb, idx);
      } else {
        const t = (y - gy) / Math.max(1, LH - gy - 1);
        const sc = t * (floorSteps - 1);
        const lo = Math.floor(sc);
        const idx = (sc - lo > m ? lo + 1 : lo) / (floorSteps - 1);
        r = lerp(ftr, fbr, idx);
        g = lerp(ftg, fbg, idx);
        b = lerp(ftb, fbb, idx);
      }
      const i = (y * LW + x) * 4;
      d[i] = r;
      d[i + 1] = g;
      d[i + 2] = b;
      d[i + 3] = 255;
    }
  }
  ctx.putImageData(img, 0, 0);

  // floorboard seams + a parting line where wall meets floor
  ctx.fillStyle = theme.wood.dark;
  ctx.fillRect(0, gy - 1, LW, 1);
  for (let y = gy + 8; y < LH; y += 10) ctx.fillRect(0, y, LW, 1);
}

// A soft radial glow (sconces / openings).
function paintGlow(ctx: CanvasRenderingContext2D, x: number, y: number, r: number, color: string, a: number) {
  const g = ctx.createRadialGradient(x, y, 1, x, y, r);
  g.addColorStop(0, color);
  g.addColorStop(1, "rgba(0,0,0,0)");
  ctx.save();
  ctx.globalAlpha = a;
  ctx.fillStyle = g;
  ctx.beginPath();
  ctx.arc(x, y, r, 0, Math.PI * 2);
  ctx.fill();
  ctx.restore();
}

// Tall crimson drape with folds.
function paintDrape(ctx: CanvasRenderingContext2D, x: number, w: number, top: number, bot: number) {
  ctx.fillStyle = "#2a0608";
  ctx.fillRect(x, top, w, bot - top);
  ctx.fillStyle = "#48101a";
  for (let i = 0; i < w; i += 5) ctx.fillRect(x + i, top, 2, bot - top); // fold highlights
  ctx.fillStyle = "#120304";
  for (let i = 3; i < w; i += 5) ctx.fillRect(x + i, top, 1, bot - top); // fold shadows
  ctx.fillStyle = "#160a0c";
  ctx.fillRect(x - 1, top, w + 2, 3); // valance rod
}

// Gothic hall: drapes, sconces, a central grand staircase, and a full-width
// balcony with a baluster railing across the upper third.
function paintHallScene(ctx: CanvasRenderingContext2D, theme: Theme) {
  const gy = Math.floor(theme.groundY / PIXEL); // 170
  const W = LW;

  paintHallWallFloor(ctx, theme);

  // flanking drapes
  paintDrape(ctx, 0, 26, 6, gy);
  paintDrape(ctx, W - 26, 26, 6, gy);

  // wall sconces (dim red glows) on the drapes down each side
  for (const sx of [18, W - 18]) {
    for (const sy of [54, 110]) {
      paintGlow(ctx, sx, sy, 22, "#b8281f", 0.45);
      ctx.fillStyle = "#3a0a0c";
      ctx.fillRect(sx - 2, sy - 4, 4, 8); // sconce bracket
      ctx.fillStyle = "#ffb648";
      ctx.fillRect(sx - 1, sy - 6, 2, 3); // flame
    }
  }

  // central grand staircase rising from the floor to the balcony
  const cx = W / 2;
  const steps = 7;
  const stepH = 5;
  const stepTop = gy - steps * stepH; // top of stairs meets balcony underside
  for (let s = 0; s < steps; s++) {
    const halfW = 20 + s * 5; // widens toward the bottom
    const y = stepTop + s * stepH;
    ctx.fillStyle = theme.wood.mid;
    ctx.fillRect(cx - halfW, y, halfW * 2, stepH);
    ctx.fillStyle = theme.wood.light;
    ctx.fillRect(cx - halfW, y, halfW * 2, 1); // step nose highlight
    ctx.fillStyle = theme.wood.dark;
    ctx.fillRect(cx - halfW, y + stepH - 1, halfW * 2, 1);
  }
  // red carpet runner down the stairs
  ctx.fillStyle = "#7a141a";
  for (let s = 0; s < steps; s++) ctx.fillRect(cx - 8, stepTop + s * stepH, 16, stepH);

  // full-width balcony platform + baluster railing across the upper third
  const balY = 84; // platform top
  ctx.fillStyle = theme.wood.dark;
  ctx.fillRect(0, balY + 10, W, 6); // platform slab (front face)
  ctx.fillStyle = theme.wood.mid;
  ctx.fillRect(0, balY + 9, W, 1); // top edge highlight
  // railing: top rail + balusters (with a gap over the central staircase)
  ctx.fillStyle = theme.wood.light;
  ctx.fillRect(0, balY, W, 2); // top rail
  ctx.fillStyle = theme.wood.mid;
  for (let x = 3; x < W; x += 7) {
    if (Math.abs(x - cx) < 22) continue; // opening above the staircase
    ctx.fillRect(x, balY + 2, 2, 8); // baluster
  }

  // doorways/windows (blood-red glow via theme.windowGlow), then grain
  for (const slot of theme.slots) drawSlot(ctx, slot, theme);
  applyGrain(ctx);
}

// Bake the full static scene at lo-res, then upscale x PIXEL (nearest) to a
// data URL (PNG) for loadSprite(). The theme supplies its own paint routine.
export function paintBackgroundDataURL(theme: Theme): string {
  const lo = document.createElement("canvas");
  lo.width = LW;
  lo.height = LH;
  const lctx = lo.getContext("2d");
  if (!lctx) throw new Error("2d context unavailable");
  lctx.imageSmoothingEnabled = false;

  theme.paint(lctx, theme);

  const out = document.createElement("canvas");
  out.width = BG_WIDTH;
  out.height = BG_HEIGHT;
  const octx = out.getContext("2d");
  if (!octx) throw new Error("2d context unavailable");
  octx.imageSmoothingEnabled = false;
  octx.drawImage(lo, 0, 0, LW, LH, 0, 0, BG_WIDTH, BG_HEIGHT);
  theme.paintHi?.(octx, theme); // full-res overlay (e.g. the house.png art)
  return out.toDataURL();
}
